export type SortDirection = 'ascending' | 'descending'
type SortType = 'text' | 'number'

const STATUS_COPY: Record<string, Record<SortDirection, string>> = {
  en: { ascending: 'Sorted by {column}, ascending.', descending: 'Sorted by {column}, descending.' },
  fr: { ascending: 'Trié par {column}, ordre croissant.', descending: 'Trié par {column}, ordre décroissant.' },
  nl: { ascending: 'Gesorteerd op {column}, oplopend.', descending: 'Gesorteerd op {column}, aflopend.' },
  de: { ascending: 'Nach {column} aufsteigend sortiert.', descending: 'Nach {column} absteigend sortiert.' },
}

function sortValue(row: HTMLTableRowElement, columnIndex: number, type: SortType) {
  const cell = row.cells[columnIndex]
  const raw = cell?.dataset.v ?? cell?.dataset.sortValue ?? cell?.textContent?.trim()
  if (!raw) return { missing: true, value: '' }
  if (type === 'number') {
    const numeric = Number(raw)
    return Number.isFinite(numeric)
      ? { missing: false, value: numeric }
      : { missing: true, value: Number.NEGATIVE_INFINITY }
  }
  return { missing: false, value: raw }
}

export function nextSortDirection(current: string | null, type: SortType): SortDirection {
  if (current === 'ascending') return 'descending'
  if (current === 'descending') return 'ascending'
  return type === 'number' ? 'descending' : 'ascending'
}

function updateRanks(table: HTMLTableElement, rows: HTMLTableRowElement[]) {
  const selector = table.dataset.sortRenumber
  if (!selector) return
  rows.forEach((row, index) => {
    const rank = row.querySelector<HTMLElement>(selector)
    if (rank) rank.textContent = String(index + 1)
  })
}

function sortTable(table: HTMLTableElement, button: HTMLButtonElement) {
  const header = button.closest<HTMLTableCellElement>('th')
  const type: SortType = header?.hasAttribute('data-sort-number') ? 'number' : 'text'
  const body = table.tBodies[0]
  if (!header || !body) return

  const direction = nextSortDirection(header.getAttribute('aria-sort'), type)
  const locale = document.documentElement.lang || undefined
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' })
  const rows = Array.from(body.rows)
  const originalOrder = new Map(rows.map((row, index) => [row, index]))
  const multiplier = direction === 'ascending' ? 1 : -1

  rows.sort((left, right) => {
    const leftValue = sortValue(left, header.cellIndex, type)
    const rightValue = sortValue(right, header.cellIndex, type)
    if (leftValue.missing || rightValue.missing) {
      if (leftValue.missing && rightValue.missing) return (originalOrder.get(left) ?? 0) - (originalOrder.get(right) ?? 0)
      return leftValue.missing ? 1 : -1
    }
    const comparison = typeof leftValue.value === 'number' && typeof rightValue.value === 'number'
      ? leftValue.value - rightValue.value
      : collator.compare(String(leftValue.value), String(rightValue.value))
    return comparison === 0
      ? (originalOrder.get(left) ?? 0) - (originalOrder.get(right) ?? 0)
      : comparison * multiplier
  })

  rows.forEach((row) => body.append(row))
  updateRanks(table, rows)
  table.querySelectorAll<HTMLTableCellElement>('thead th[aria-sort]').forEach((cell) => cell.setAttribute('aria-sort', 'none'))
  header.setAttribute('aria-sort', direction)

  const label = button.textContent?.trim() ?? ''
  const language = (document.documentElement.lang || 'en').split('-')[0]
  const template = (STATUS_COPY[language] ?? STATUS_COPY.en)[direction]
  const container = table.closest('[data-sort-container]') ?? table.parentElement
  let status = container?.querySelector<HTMLElement>('[data-sort-status]') ?? null
  if (!status && container) {
    status = document.createElement('p')
    status.className = 'sr-only'
    status.dataset.sortStatus = ''
    status.setAttribute('aria-live', 'polite')
    status.setAttribute('aria-atomic', 'true')
    container.append(status)
  }
  if (status) status.textContent = template.replace('{column}', label)
}

export function initializeSortableTables(root: ParentNode = document) {
  root.querySelectorAll<HTMLTableElement>('[data-sortable-table]').forEach((table) => {
    if (table.dataset.sortInitialized === 'true') return
    table.dataset.sortInitialized = 'true'
    table.addEventListener('click', (event) => {
      const target = event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>('[data-sort-button]')
        : null
      if (target && table.contains(target)) sortTable(table, target)
    })
  })
}
